import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { laborDemandForecast } from "@/lib/labor-forecast.functions";
import { useScope } from "@/hooks/use-scope";
import { PageHeader } from "@/components/ui/page-header";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { CalendarClock, AlertTriangle } from "lucide-react";

export const Route = createFileRoute("/_authenticated/app/labor/forecast")({ component: LaborForecast });

const HORIZONS = [3, 7, 14] as const;
const ACTIVITIES = ["pick", "pack", "receive", "putaway", "count"] as const;

function fmtH(h: number) { return h.toFixed(1) + "h"; }
function fmt$(c: number) { return "$" + (c / 100).toLocaleString(undefined, { maximumFractionDigits: 0 }); }
function fmtDay(ds: string) {
  const d = new Date(`${ds}T00:00:00Z`);
  return d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric", timeZone: "UTC" });
}

function LaborForecast() {
  const { tenantId, facilityId } = useScope();
  const fn = useServerFn(laborDemandForecast);
  const [horizonDays, setHorizonDays] = useState<number>(7);

  const { data, isLoading } = useQuery({
    queryKey: ["labor-forecast", tenantId, facilityId, horizonDays],
    enabled: !!tenantId,
    queryFn: () =>
      fn({ data: { tenantId: tenantId!, facilityId: facilityId ?? null, horizonDays } }),
    placeholderData: keepPreviousData,
  });

  const days = data?.days ?? [];
  const totals = data?.totals ?? { hours: 0, cost_cents: 0, volume: 0 };
  const noBaseline = !isLoading && (data?.sample_event_count ?? 0) === 0;

  return (
    <>
      <PageHeader
        title="Demand Forecast"
        description="Projected labor hours and cost for the next planning window, using trailing productivity per activity."
        actions={
          <Tabs value={String(horizonDays)} onValueChange={(v) => setHorizonDays(Number(v))}>
            <TabsList>
              {HORIZONS.map((h) => (
                <TabsTrigger key={h} value={String(h)}>{h}d</TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
        }
      />
      <div className="p-4 sm:p-6 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <SummaryCard label="Projected volume" value={`${totals.volume} orders`} icon={<CalendarClock className="h-4 w-4" />} />
          <SummaryCard label="Projected hours" value={fmtH(totals.hours)} />
          <SummaryCard label="Projected cost" value={fmt$(totals.cost_cents)} />
        </div>

        {noBaseline && (
          <Card>
            <CardContent className="py-6 flex items-start gap-2 text-sm text-muted-foreground">
              <AlertTriangle className="h-4 w-4 mt-0.5 text-amber-500" />
              No labor events in the lookback window — forecast assumes zero seconds per order until operators clock activity.
            </CardContent>
          </Card>
        )}

        <Card>
          <CardHeader><CardTitle className="text-base">Daily Projection</CardTitle></CardHeader>
          <CardContent className="p-0 overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Day</TableHead>
                  <TableHead className="text-right">Orders</TableHead>
                  {ACTIVITIES.map((a) => (
                    <TableHead key={a} className="text-right capitalize">{a}</TableHead>
                  ))}
                  <TableHead className="text-right">Total hrs</TableHead>
                  <TableHead className="text-right">Cost</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow><TableCell colSpan={ACTIVITIES.length + 4} className="text-center py-8 text-muted-foreground">Loading…</TableCell></TableRow>
                ) : days.length === 0 ? (
                  <TableRow><TableCell colSpan={ACTIVITIES.length + 4} className="text-center py-8 text-muted-foreground">No forecast available.</TableCell></TableRow>
                ) : days.map((d) => {
                  const dayHours = d.rows.reduce((s, r) => s + r.hours, 0);
                  const dayCost = d.rows.reduce((s, r) => s + r.cost_cents, 0);
                  return (
                    <TableRow key={d.date}>
                      <TableCell className="font-medium">{fmtDay(d.date)}</TableCell>
                      <TableCell className="text-right tabular-nums">{d.volume}</TableCell>
                      {ACTIVITIES.map((a) => {
                        const row = d.rows.find((r) => r.activity === a);
                        return (
                          <TableCell key={a} className="text-right tabular-nums text-muted-foreground">
                            {row ? fmtH(row.hours) : "—"}
                          </TableCell>
                        );
                      })}
                      <TableCell className="text-right tabular-nums">{fmtH(dayHours)}</TableCell>
                      <TableCell className="text-right tabular-nums">{fmt$(dayCost)}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {data && (
          <p className="text-xs text-muted-foreground">
            Baseline: trailing {data.lookback_days}d ({data.sample_event_count} labor events,
            {" "}{data.avg_orders_per_day.toFixed(1)} orders/day). Scheduled orders for a given day override the baseline.
          </p>
        )}
      </div>
    </>
  );
}

function SummaryCard({ label, value, icon }: { label: string; value: string; icon?: React.ReactNode }) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
          {icon}{label}
        </CardTitle>
      </CardHeader>
      <CardContent className="text-2xl font-semibold tabular-nums">{value}</CardContent>
    </Card>
  );
}